import React, { useState, useEffect } from 'react';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import { addSchedulesBatch, getTutorSchedules, deleteSchedule, subscribeToSchedules, subscribeToMemos } from '../services/firestoreService';
import '../styles/ScheduleRegister.css';

// 공통 날짜 계산 함수 (관리자 기준 - 월요일 시작)
const getWeekCalculation = (baseDate = new Date()) => {
  const today = new Date(baseDate);
  const currentDay = today.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
  
  // 이번 주 월요일 계산 (월요일을 주의 시작으로 함)
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // 일요일인 경우 -6, 나머지는 1-currentDay
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);
  
  // 다음 주 월요일 계산
  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  
  return {
    monday,
    nextMonday,
    getDateForDayOfWeek: (dayOfWeek) => {
      const dayOfWeekMap = {
        'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3,
        'friday': 4, 'saturday': 5, 'sunday': 6
      };
      const targetDayIndex = dayOfWeekMap[dayOfWeek];
      const targetDate = new Date(monday);
      targetDate.setDate(monday.getDate() + targetDayIndex);
      return targetDate;
    }
  };
};

const ScheduleRegister = ({ user }) => {
  const { alertState, showSuccess, showError, showWarning, showConfirm, showInfo } = useCustomAlert();
  const [selectedDays, setSelectedDays] = useState([]);
  const [scheduleData, setScheduleData] = useState({});
  const [originalSchedules, setOriginalSchedules] = useState([]); // 기존 스케줄 정보 저장
  const [memos, setMemos] = useState([]); // 매칭대기 메모 저장
  const [loading, setLoading] = useState(true);
  
  // 요일 리스트
  const weekDays = [
    { id: 'monday', name: '월요일', short: '월' },
    { id: 'tuesday', name: '화요일', short: '화' },
    { id: 'wednesday', name: '수요일', short: '수' },
    { id: 'thursday', name: '목요일', short: '목' },
    { id: 'friday', name: '금요일', short: '금' },
    { id: 'saturday', name: '토요일', short: '토' },
    { id: 'sunday', name: '일요일', short: '일' }
  ];
  
  // 시간대 리스트 (08:00 ~ 22:00)
  const timeSlots = [
    '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
    '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
    '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00',
    '20:00-21:00', '21:00-22:00'
  ];

  // 실시간 스케줄 구독 및 기존 스케줄 로드
  useEffect(() => {
    let unsubscribe;
    
    const setupRealTimeSubscription = () => {
      if (!user?.email) return;
      
      setLoading(true);
      
      unsubscribe = subscribeToSchedules((allSchedules) => {
        try {
          const tutorKeys = [(user.email || '').toLowerCase()];
          // 현재 튜터의 스케줄만 필터링
          const tutorSchedules = allSchedules.filter(schedule => 
            schedule && tutorKeys.includes((schedule.tutorId || '').toLowerCase())
          );
          
          // 기존 스케줄 정보 저장 (삭제 처리를 위해)
          setOriginalSchedules(tutorSchedules);
          
          // 기존 스케줄을 scheduleData 형태로 변환
          const loadedData = {};
          const loadedDays = [];
          
          tutorSchedules.forEach(schedule => {
            const { dayOfWeek, timeSlot, status } = schedule;
            
            if (!loadedData[dayOfWeek]) {
              loadedData[dayOfWeek] = {};
            }
            
            loadedData[dayOfWeek][timeSlot] = status;
            
            // 선택된 요일 목록에 추가 (중복 방지)
            if (!loadedDays.includes(dayOfWeek)) {
              loadedDays.push(dayOfWeek);
            }
          });
          
          setScheduleData(loadedData);
          setSelectedDays(loadedDays);
          setLoading(false);
        } catch (error) {
          console.error('실시간 스케줄 처리 오류:', error);
          setLoading(false);
        }
      }, (error) => {
        console.error('실시간 스케줄 구독 오류:', error);
        setLoading(false);
      });
    };

    setupRealTimeSubscription();

    // 컴포넌트 언마운트 시 구독 해제
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.email]);

  // 매칭대기 메모 구독
  useEffect(() => {
    if (!user?.email) return;

    console.log('🔄 매칭대기 메모 구독 시작:', user.email);

    const unsubscribeMemos = subscribeToMemos((allMemos) => {
      try {
        const keys = [(user.email || '').toLowerCase()];
        // pending_memo 타입의 메모만 필터링
        const pendingMemos = allMemos.filter(memo => 
          memo && 
          keys.includes((memo.tutorId || '').toLowerCase()) && 
          memo.memoType === 'pending_memo'
        );
        
        console.log('📝 희망시간등록 - 매칭대기 메모 업데이트:', {
          전체메모수: allMemos.length,
          튜터메모수: allMemos.filter(m => m && (m.tutorId || '').toLowerCase() === (user.email || '').toLowerCase()).length,
          매칭대기메모수: pendingMemos.length,
          매칭대기메모들: pendingMemos
        });
        
        setMemos(pendingMemos);
      } catch (error) {
        console.error('❌ 매칭대기 메모 처리 오류:', error);
      }
    });

    return () => {
      console.log('🔄 매칭대기 메모 구독 해제');
      unsubscribeMemos();
    };
  }, [user?.email]);

  // 매칭 가능 여부 확인 함수 (1주일 전 기준)
  const isMatchingUnavailable = (dayId, timeSlot) => {
    // 해당 요일과 시간대의 매칭대기 메모 찾기
    const pendingMemo = memos.find(memo => 
      memo && 
      memo.dayOfWeek === dayId && 
      (memo.timeSlot === timeSlot || memo.time === timeSlot)
    );

    if (!pendingMemo || !pendingMemo.unavailableDates) {
      return false;
    }

    // 공통 날짜 계산 사용
    const { getDateForDayOfWeek } = getWeekCalculation();
    
    // 다음 주 해당 요일 날짜 (시작)
    const nextWeekStart = getDateForDayOfWeek(dayId);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7); // 다음 주로
    nextWeekStart.setHours(0, 0, 0, 0);
    
    // 다음 주 해당 요일 날짜 (끝)
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setDate(nextWeekEnd.getDate() + 6); // 다음 주 마지막 날
    nextWeekEnd.setHours(23, 59, 59, 999);

    // 불가능한 날짜들 중에서 다음 주 (7일 이내)에 오는 날짜가 있는지 확인
    const result = pendingMemo.unavailableDates.some(dateStr => {
      const unavailableDate = new Date(dateStr);
      unavailableDate.setHours(0, 0, 0, 0);
      
      // 다음 주 범위 내에 있는지 확인
      return unavailableDate >= nextWeekStart && unavailableDate <= nextWeekEnd;
    });
    
    return result;
  };

  
  // 요일 선택/해제
  const toggleDay = async (dayId) => {
    setSelectedDays(prev => {
      if (prev.includes(dayId)) {
        // 요일 제거 시 선택 목록에서 제거하고 해당 요일 데이터도 제거
        setScheduleData(prevData => {
          const newData = { ...prevData };
          delete newData[dayId];
          return newData;
        });
        return prev.filter(d => d !== dayId);
      } else {
        // 요일 선택 시 DB에서 최신 데이터를 가져와서 해당 요일 데이터 업데이트
        loadDayDataFromDB(dayId);
        return [...prev, dayId];
      }
    });
  };

  // DB에서 특정 요일의 최신 데이터를 가져오는 함수
  const loadDayDataFromDB = async (dayId) => {
    try {
      const existingSchedules = await getTutorSchedules(user.email || user.userId);
      
      // 해당 요일의 스케줄만 필터링
      const daySchedules = existingSchedules.filter(schedule => schedule.dayOfWeek === dayId);
      
      // scheduleData에 해당 요일 데이터 업데이트
      setScheduleData(prev => {
        const newScheduleData = { ...prev };
        const dayData = {};
        
        daySchedules.forEach(schedule => {
          dayData[schedule.timeSlot] = schedule.status;
        });
        
        newScheduleData[dayId] = dayData;
        return newScheduleData;
      });
      
      // originalSchedules도 업데이트 (기존 데이터와 병합)
      setOriginalSchedules(prev => {
        const existingIds = prev.map(s => s.id);
        const newSchedules = daySchedules.filter(s => !existingIds.includes(s.id));
        return [...prev, ...newSchedules];
      });
      
    } catch (error) {
      console.error('요일 데이터 로드 오류:', error);
      showError('스케줄 데이터를 불러오는데 실패했습니다.');
    }
  };

        // 시간대 토글 (매칭 대기만 수정 가능)
  const toggleTimeSlot = (dayId, timeSlot) => {
    setScheduleData(prev => {
      const dayData = prev[dayId] || {};
      const currentStatus = dayData[timeSlot];
      
      // 확정완료나 메모등록 상태는 수정 불가
      if (currentStatus === 'confirmed' || currentStatus === 'registered') {
        return prev;
      }
      
      const newDayData = { ...dayData };
      
      if (currentStatus === 'pending') {
        // 매칭 대기 상태인 경우 취소 (none으로 설정)
        newDayData[timeSlot] = 'none';
      } else if (!currentStatus || currentStatus === 'none') {
                  // 선택되지 않은 경우 매칭 대기로 설정
        newDayData[timeSlot] = 'pending';
      }
      
      return {
        ...prev,
        [dayId]: newDayData
      };
    });
  };

        // 요일별 전체 선택/해제 (매칭 대기로 설정)
  const toggleAllTimesForDay = (dayId) => {
    const dayData = scheduleData[dayId] || {};
    
    // pending 상태인 시간대만 확인 (confirmed, registered 제외)
    const pendingTimeSlots = timeSlots.filter(time => {
      return dayData[time] === 'pending';
    });
    
    // 수정 가능한 시간대 개수 (confirmed, registered 제외)
    const editableTimeSlots = timeSlots.filter(time => {
      const currentStatus = dayData[time];
      return currentStatus !== 'confirmed' && currentStatus !== 'registered';
    });
    
    // 모든 수정 가능한 시간대가 pending 상태인지 확인
    const allPendingSelected = editableTimeSlots.length > 0 && 
      editableTimeSlots.every(time => dayData[time] === 'pending');
    
    setScheduleData(prev => {
      const newDayData = { ...dayData }; // 기존 데이터 복사
      
      timeSlots.forEach(time => {
        const currentStatus = dayData[time];
        
        // confirmed나 registered 상태는 그대로 유지
        if (currentStatus === 'confirmed' || currentStatus === 'registered') {
          // 아무것도 하지 않음 (기존 상태 유지)
        } else if (allPendingSelected) {
          // 모든 pending이 선택된 상태면 pending 취소 (none으로 설정)
          newDayData[time] = 'none';
        } else {
          // 그렇지 않으면 pending으로 설정
          newDayData[time] = 'pending';
        }
      });
      
      return {
        ...prev,
        [dayId]: newDayData
      };
    });
  };

  // 전체 평일/주말 선택
  const selectWeekdays = async () => {
    const weekdayIds = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    setSelectedDays(weekdayIds);
    
    // 모든 평일 데이터를 DB에서 가져와서 업데이트
    await loadMultipleDaysDataFromDB(weekdayIds);
  };

  const selectWeekends = async () => {
    const weekendIds = ['saturday', 'sunday'];
    setSelectedDays(weekendIds);
    
    // 모든 주말 데이터를 DB에서 가져와서 업데이트
    await loadMultipleDaysDataFromDB(weekendIds);
  };

  // 여러 요일의 데이터를 DB에서 가져오는 함수
  const loadMultipleDaysDataFromDB = async (dayIds) => {
    try {
      const existingSchedules = await getTutorSchedules(user.email || user.userId);
      
      setScheduleData(prev => {
        const newScheduleData = { ...prev };
        
        dayIds.forEach(dayId => {
          // 해당 요일의 스케줄만 필터링
          const daySchedules = existingSchedules.filter(schedule => schedule.dayOfWeek === dayId);
          
          const dayData = {};
          daySchedules.forEach(schedule => {
            dayData[schedule.timeSlot] = schedule.status;
          });
          
          newScheduleData[dayId] = dayData;
        });
        
        return newScheduleData;
      });
      
      // originalSchedules도 업데이트
      setOriginalSchedules(prev => {
        const existingIds = prev.map(s => s.id);
        const newSchedules = existingSchedules.filter(s => !existingIds.includes(s.id));
        return [...prev, ...newSchedules];
      });
      
    } catch (error) {
      console.error('요일 데이터 로드 오류:', error);
      showError('스케줄 데이터를 불러오는데 실패했습니다.');
    }
  };

    // 저장하기 (실제 Firestore에 저장)
  const handleSave = async () => {
    try {
      // 1. 현재 선택된 pending 스케줄들
      const currentPendingSchedules = [];
      Object.keys(scheduleData).forEach(dayId => {
        Object.keys(scheduleData[dayId]).forEach(timeSlot => {
          if (scheduleData[dayId][timeSlot] === 'pending') {
            currentPendingSchedules.push(`${dayId}-${timeSlot}`);
          }
        });
      });

      // 2. 기존 pending 스케줄들
      const originalPendingSchedules = originalSchedules
        .filter(schedule => schedule.status === 'pending')
        .map(schedule => `${schedule.dayOfWeek}-${schedule.timeSlot}`);

      // 3. 실제 변경사항 계산
      const schedulesToDelete = originalPendingSchedules.filter(
        schedule => !currentPendingSchedules.includes(schedule)
      );
      
      const schedulesToAdd = currentPendingSchedules.filter(
        schedule => !originalPendingSchedules.includes(schedule)
      );

      console.log('🗑️ 삭제할 스케줄:', schedulesToDelete);
      console.log('➕ 추가할 스케줄:', schedulesToAdd);
      console.log('📊 기존 pending 스케줄:', originalPendingSchedules);
      console.log('📊 현재 pending 스케줄:', currentPendingSchedules);

      // 4. 삭제할 스케줄들 삭제
      for (const scheduleKey of schedulesToDelete) {
        const parts = scheduleKey.split('-');
        const dayOfWeek = parts[0];
        const timeSlot = parts.slice(1).join('-'); // 나머지 부분을 모두 합쳐서 timeSlot으로 사용
        
        const scheduleToDelete = originalSchedules.find(
          schedule => schedule.dayOfWeek === dayOfWeek && 
                     schedule.timeSlot === timeSlot && 
                     schedule.status === 'pending'
        );
        if (scheduleToDelete) {
          console.log('🗑️ 실제 삭제:', scheduleToDelete);
          await deleteSchedule(scheduleToDelete.id);
        } else {
          console.log('❌ 삭제할 스케줄을 찾을 수 없음:', scheduleKey);
          console.log('🔍 찾는 조건:', { dayOfWeek, timeSlot });
          console.log('📋 기존 스케줄들:', originalSchedules.filter(s => s.dayOfWeek === dayOfWeek));
        }
      }

      // 5. 추가할 스케줄들 생성
      const schedulesToSave = [];
      for (const scheduleKey of schedulesToAdd) {
        const parts = scheduleKey.split('-');
        const dayOfWeek = parts[0];
        const timeSlot = parts.slice(1).join('-'); // 나머지 부분을 모두 합쳐서 timeSlot으로 사용
        
        schedulesToSave.push({
          tutorId: user.email,
          tutorName: user.name,
          dayOfWeek: dayOfWeek,
          timeSlot: timeSlot,
          status: 'pending',
          requestedBy: user.email
        });
      }

      if (schedulesToSave.length > 0) {
        await addSchedulesBatch(schedulesToSave);
      }

      // 6. 실시간 구독으로 자동 업데이트되므로 수동 로드 제거

      // 8. 변경사항이 있을 때만 메시지 표시
      if (schedulesToDelete.length > 0 || schedulesToAdd.length > 0) {
        showSuccess(`스케줄이 저장되었습니다!\n삭제: ${schedulesToDelete.length}개, 생성: ${schedulesToAdd.length}개`);
      } else {
        showSuccess('스케줄이 저장되었습니다!\n변경사항이 없습니다.');
      }
    } catch (error) {
      console.error('스케줄 저장 오류:', error);
      showError('스케줄 저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 상태별 텍스트 및 스타일
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { text: '매칭 대기', class: 'pending', icon: '⏳', editable: true };
      case 'registered':
        return { text: '메모 등록', class: 'registered', icon: '📝', editable: false };
      case 'confirmed':
        return { text: '확정 완료', class: 'confirmed', icon: '✅', editable: false };
      default:
        return { text: '선택 안됨', class: 'none', icon: '⚪', editable: true };
    }
  };



  // 변경사항이 있는지 확인
  const hasChanges = () => {
    // 현재 상태의 pending 스케줄들
    const currentPendingSchedules = [];
    Object.keys(scheduleData).forEach(dayId => {
      Object.keys(scheduleData[dayId]).forEach(timeSlot => {
        if (scheduleData[dayId][timeSlot] === 'pending') {
          currentPendingSchedules.push(`${dayId}-${timeSlot}`);
        }
      });
    });

    // 기존 pending 스케줄들만
    const originalPendingSchedules = originalSchedules
      .filter(schedule => schedule.status === 'pending')
      .map(schedule => `${schedule.dayOfWeek}-${schedule.timeSlot}`);

    // 배열을 정렬해서 비교
    const currentSorted = currentPendingSchedules.sort();
    const originalSorted = originalPendingSchedules.sort();

    return JSON.stringify(currentSorted) !== JSON.stringify(originalSorted);
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="schedule-register-container">
        <div className="page-header">
          <h1>📅 희망 스케줄 등록</h1>
          <p>기존 스케줄을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-register-container">
      <div className="page-header">
        <h1>📅 희망 스케줄 등록</h1>
        <p>반복적으로 수업 가능한 요일과 시간대를 설정하세요</p>
        <div className="info-note">
          <span>💡 등록 후 관리자가 학생을 배정하면 확정됩니다</span>
        </div>
      </div>

      <div className="register-content">
        {/* 요일 선택 */}
        <div className="day-section">
          <div className="section-header">
            <h3>📆 요일 선택</h3>
            <div className="quick-select">
              <button type="button" onClick={selectWeekdays} className="quick-btn weekday">
                평일 선택
              </button>
              <button type="button" onClick={selectWeekends} className="quick-btn weekend">
                주말 선택
              </button>
            </div>
          </div>
          
          <div className="day-grid">
            {weekDays.map((day) => (
              <div
                key={day.id}
                className={`day-card ${selectedDays.includes(day.id) ? 'selected' : ''}`}
                onClick={() => toggleDay(day.id)}
              >
                <div className="day-short">{day.short}</div>
                <div className="day-name">{day.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 선택된 요일별 시간대 설정 */}
        {weekDays.filter(day => selectedDays.includes(day.id)).map((day) => {
          const dayId = day.id;
          const dayData = scheduleData[dayId] || {};
          
          return (
            <div key={dayId} className="time-section">
              <div className="time-header">
                <h3>⏰ {day.name} 시간대</h3>
                <div className="time-controls">
                  <button 
                    type="button"
                    onClick={() => toggleAllTimesForDay(dayId)}
                    className="toggle-all-btn"
                  >
                    {(() => {
                      // 수정 가능한 시간대 개수 (confirmed, registered 제외)
                      const editableTimeSlots = timeSlots.filter(time => {
                        const currentStatus = dayData[time];
                        return currentStatus !== 'confirmed' && currentStatus !== 'registered';
                      });
                      
                      // 수정 가능한 시간대 중 pending 상태인 개수
                      const editablePendingCount = editableTimeSlots.filter(time => 
                        dayData[time] === 'pending'
                      ).length;
                      
                      return editablePendingCount === editableTimeSlots.length && editableTimeSlots.length > 0 
                        ? '전체 해제' 
                        : '전체 선택';
                    })()}
                  </button>
                </div>
              </div>

              <div className="time-grid">
                {timeSlots.map((time) => {
                  // 매칭대기 메모 확인
                  const hasPendingMemo = memos.some(memo => {
                    const match = memo && 
                      memo.dayOfWeek === dayId && 
                      (memo.timeSlot === time || memo.time === time);
                    
                    return match;
                  });
                  
                  // 기본 상태 결정
                  let status = dayData[time] || 'none';
                  const originalStatus = status;
                  
                  // 매칭대기 메모가 있으면 항상 pending_with_memo 상태로 통일
                  if (hasPendingMemo) {
                    if (status === 'none') {
                      status = 'pending_with_memo';  // 선택 안함 → 매칭대기 메모
                    } else if (status === 'pending_with_memo') {
                      status = 'pending_with_memo';  // 항상 매칭대기 메모 상태
                    }
                  }
                  
                  const statusInfo = getStatusInfo(status);
                  const isClickable = status === 'none' || status === 'pending';
                  
                  // 매칭 불가능 여부 확인 (1주일 전 기준)
                  const isUnavailable = isMatchingUnavailable(dayId, time);
                  
                  // 매칭대기 메모가 있는 경우 클릭 불가능하게 설정
                  const hasMemoRestriction = hasPendingMemo && status === 'pending';
                  
                  // 최종 클릭 가능 여부 결정
                  const finalIsClickable = isClickable && !isUnavailable && !hasMemoRestriction;
                  
                  // 클래스명 결정
                  let className = `time-slot ${statusInfo.class}`;
                  
                  if (status === 'pending_with_memo') {
                    // 매칭대기 메모 상태는 항상 동일한 스타일 적용
                    className += ' unavailable-memo';
                  } else if (!isClickable && (status === 'confirmed' || status === 'registered')) {
                    // 원래부터 클릭 불가능한 경우 (confirmed, registered)
                    className += ' disabled';
                  }
                  
                  return (
                    <div
                      key={time}
                      className={className}
                      onClick={() => finalIsClickable && toggleTimeSlot(dayId, time)}
                      title={
                        status === 'pending_with_memo'
                          ? '매칭대기 메모가 등록되어 있습니다. 대시보드에서 메모를 삭제한 후 취소 가능합니다.'
                          : !isClickable && (status === 'confirmed' || status === 'registered')
                          ? '관리자가 처리한 스케줄은 수정할 수 없습니다'
                          : finalIsClickable
                          ? status === 'pending'
                            ? '클릭하여 취소'
                            : '클릭하여 매칭 대기로 설정'
                          : ''
                      }
                    >
                      <div className="time-display">{time}</div>
                      <div className="time-status">
                        {status === 'pending_with_memo'
                          ? '⏳📝 매칭대기 메모' 
                          : `${statusInfo.icon} ${statusInfo.text}`
                        }
                      </div>
                      {!isClickable && (status === 'confirmed' || status === 'registered') && (
                        <div className="lock-indicator">🔒</div>
                      )}
                      {status === 'pending_with_memo' && (
                        <div className="memo-indicator">📝</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* 저장 버튼 */}
        {selectedDays.length > 0 && (
          <div className="register-actions">
            {hasChanges() && (
              <button
                onClick={handleSave}
                className="save-btn"
              >
                💾 변경사항 저장하기
              </button>
            )}
            {!hasChanges() && (
              <div className="no-changes-message">
                ✅ 변경사항이 없습니다
              </div>
            )}
            
            <div className="summary">
              <h4>📋 등록 현황</h4>
              <p>선택된 요일: {selectedDays.length}개 ({selectedDays.map(id => weekDays.find(d => d.id === id)?.short).join(', ')})</p>
              
              <div className="legend">
                <div className="legend-item">
                  <span className="legend-color none"></span>
                  <span>⚪ 선택 안됨 (클릭으로 선택 가능)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color pending"></span>
                  <span>⏳ 매칭 대기 (클릭으로 선택/해제 가능)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color pending"></span>
                  <span>⏳📝 매칭대기 메모 (대시보드에서 메모 삭제 후 해제 가능)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color registered"></span>
                  <span>📝 메모 등록 (🔒 수정 불가)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color confirmed"></span>
                  <span>✅ 확정 완료 (🔒 수정 불가)</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 커스텀 알림 모달 */}
      <CustomAlert {...alertState} />
    </div>
  );
};

export default ScheduleRegister; 